import _extends from "@babel/runtime-corejs3/helpers/extends";
import _filterInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/filter";
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import { withRouter, useLocation, NavLink } from 'react-router-dom';
import { TranslatorContext, useSafeTranslatedUrls } from '@jutro/locale';
import { intlMessageShape } from '@jutro/prop-types';
import { Icon, Link } from '@jutro/components';
import { useAccessibleRef } from '@jutro/platform';
import { SideNavigationContext } from './SideNavigation/SideNavigationContext';
import { renderBadge } from './SideNavigation/renderBadge';
import styles from "./NavBarItem.module.css";
export const getIdFromPath = path => {
  var _context;
  const pathArr = _filterInstanceProperty(_context = path.split('/')).call(_context, Boolean);
  const parentPath = pathArr[0];
  const childPath = _.last(pathArr);
  return parentPath === childPath ? childPath : "".concat(parentPath, "__").concat(childPath);
};
export const NavBarLinkInternal = ({
  header,
  to: toProp,
  href,
  exact,
  icon,
  disabled,
  className,
  activeClassName,
  alignment,
  notifications
}) => {
  const _useContext = useContext(SideNavigationContext),
    setActiveRoute = _useContext.setActiveRoute;
  const translator = useContext(TranslatorContext);
  const urlTranslatorAndSanitizer = useSafeTranslatedUrls();
  const linkRef = useAccessibleRef();
  const to = urlTranslatorAndSanitizer(toProp);
  const isTopNavBar = alignment === 'top';
  const location = useLocation();
  const isActiveRoute = to === location.pathname;
  const isActiveSideNavItem = isActiveRoute && !isTopNavBar;
  const isInternalLink = !!to;
  const classes = cx(styles.navBarItem, isTopNavBar ? styles.topNavBarItem : styles.leftNavBarItem, {
    [styles.activeTopNavBarItem]: isActiveRoute && isTopNavBar,
    [styles.activeLeftNavBarItem]: isActiveSideNavItem,
    isActiveSideNavItem
  }, className);
  const activeClasses = cx(isTopNavBar ? styles.activeTopNavBarItem : styles.activeLeftNavBarItem, activeClassName);
  const iconComponent = icon && React.createElement(Icon, {
    icon: icon,
    className: styles.navBarItemIcon
  });
  const title = React.createElement("span", {
    className: styles.title
  }, translator(header));
  const content = !isTopNavBar ? React.createElement("div", {
    className: styles.titleContentWrapper
  }, title, renderBadge(notifications, isInternalLink && getIdFromPath(to))) : title;
  const clickHandler = useCallback(event => {
    var _event$persist;
    (_event$persist = event.persist) === null || _event$persist === void 0 ? void 0 : _event$persist.call(event);
    setActiveRoute(to);
  }, [setActiveRoute, to]);
  const commonLinkProps = {
    disabled,
    className: classes,
    type: 'link',
    role: 'menuitem'
  };
  return isInternalLink ? React.createElement(NavLink, _extends({}, commonLinkProps, {
    to: to,
    exact: exact,
    activeClassName: activeClasses,
    tabIndex: disabled ? -1 : 0,
    onClick: clickHandler,
    innerRef: linkRef
  }), React.createElement("div", {
    className: styles.titleWrapper
  }, iconComponent, content)) : React.createElement(Link, _extends({}, commonLinkProps, {
    ref: linkRef,
    textClassName: styles.titleWrapper,
    inline: false,
    href: href,
    target: "_blank"
  }), iconComponent, content);
};
NavBarLinkInternal.propTypes = {
  className: PropTypes.string,
  header: intlMessageShape.isRequired,
  to: intlMessageShape,
  href: intlMessageShape,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  activeClassName: PropTypes.string,
  alignment: PropTypes.oneOf(['top', 'left']),
  notifications: PropTypes.number
};
NavBarLinkInternal.displayName = 'NavBarLink';
export const NavBarLink = withRouter(NavBarLinkInternal);
NavBarLinkInternal.__docgenInfo = {
  componentName: "NavBarLink",
  packageName: "@jutro/router",
  description: "The NavBarLink component is designed as a simple component for presenting a navigation link inside the NavBar container.",
  displayName: "NavBarLink",
  methods: [],
  actualName: "NavBarLinkInternal",
  props: {
    className: {
      type: {
        name: "string"
      },
      required: false,
      description: "Additional class names for component."
    },
    header: {
      type: {
        name: "union",
        value: [{
          name: "string"
        }, {
          name: "shape",
          value: {
            id: {
              name: "string",
              required: false
            },
            defaultMessage: {
              name: "string",
              required: false
            },
            args: {
              name: "shape",
              value: {},
              required: false
            }
          }
        }]
      },
      required: true,
      description: "Link header."
    },
    to: {
      type: {
        name: "union",
        value: [{
          name: "string"
        }, {
          name: "shape",
          value: {
            id: {
              name: "string",
              required: false
            },
            defaultMessage: {
              name: "string",
              required: false
            },
            args: {
              name: "shape",
              value: {},
              required: false
            }
          }
        }]
      },
      required: false,
      description: "Internal path to the application."
    },
    href: {
      type: {
        name: "union",
        value: [{
          name: "string"
        }, {
          name: "shape",
          value: {
            id: {
              name: "string",
              required: false
            },
            defaultMessage: {
              name: "string",
              required: false
            },
            args: {
              name: "shape",
              value: {},
              required: false
            }
          }
        }]
      },
      required: false,
      description: "External path from the application."
    },
    icon: {
      type: {
        name: "string"
      },
      required: false,
      description: "Icon to render to the left of the header"
    },
    disabled: {
      type: {
        name: "bool"
      },
      required: false,
      description: "Flag to make item disabled"
    },
    activeClassName: {
      type: {
        name: "string"
      },
      required: false,
      description: "Additional class names for  active component."
    },
    alignment: {
      type: {
        name: "enum",
        value: [{
          value: "'top'",
          computed: false
        }, {
          value: "'left'",
          computed: false
        }]
      },
      required: false,
      description: "Navigation alignment"
    },
    notifications: {
      type: {
        name: "number"
      },
      required: false,
      description: "The value of the Badge"
    }
  }
};